import { FC, useEffect } from 'react'
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { SosOutlined } from '@mui/icons-material'

import Icon from 'components/Icon'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import Section from 'components/Section'
import HelpdeskListRow from 'components/Helpdesk/HelpdeskListRow'
import { getFaultGradient, getFaultStatusColors } from 'components/Helpdesk/helpdesk.utils'
import { useHelpdeskTicketsList } from 'services/helpdesk/hooks'
import { useCanReportFault } from 'components/Helpdesk/useCanReportFault'

// Fault tickets list ("Panel Klienta 3.0"): desktop master list / mobile cards.
const DefectsList: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { ticketsList, isFetching, isLoading } = useHelpdeskTicketsList()
  const { isLoading: isLeaseLoading } = useCanReportFault()

  const selectedId = matchPath('/faults/report/:id', pathname)?.params.id

  // On desktop, preselect the first ticket so the detail column is not empty.
  useEffect(() => {
    if (isDesktop && pathname === '/faults' && ticketsList.length > 0) {
      navigate(`/faults/report/${ticketsList[0].id}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, pathname, ticketsList])

  if (isLeaseLoading || isLoading) {
    return (
      <Section sx={{ flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress />
      </Section>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isDesktop ? 0 : '12px' }}>
      {!ticketsList.length && !isFetching && (
        <HelpdeskEmptyState
          Icon={SosOutlined}
          title={formatMessage({
            id: 'defectsList.noFaults',
            defaultMessage: 'Brak usterek'
          })}
          subtitle={formatMessage({
            id: 'helpdesk.noFaultsHint',
            defaultMessage: 'Nie masz żadnych zgłoszeń. Wszystko działa jak należy.'
          })}
        />
      )}
      {ticketsList.map(({ id, status, createdAt, topic, faultCategory }, index) => (
        <HelpdeskListRow
          key={id}
          onClick={() => navigate(`/faults/report/${id}`)}
          selected={isDesktop && String(id) === selectedId}
          title={topic}
          subtitle={dayjs(createdAt).format('DD.MM.YYYY')}
          badgeLabel={status.name}
          badgeColors={getFaultStatusColors(status.code, isDesktop)}
          iconSlot={
            <Box
              sx={{
                width: isDesktop ? '46px' : '48px',
                height: isDesktop ? '46px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: getFaultGradient(faultCategory?.id ?? index),
                flexShrink: 0
              }}>
              <Icon
                name={faultCategory?.iconName ?? 'QuestionMark'}
                sx={{ color: '#fff', fontSize: isDesktop ? '20px' : '24px' }}
              />
            </Box>
          }
        />
      ))}
    </Box>
  )
}

export default DefectsList
